// Step and transition inspectors use the existing process payload; no additional analysis request.
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Layers,
  ListOrdered,
  MessageCircleQuestion,
  PieChart,
  Repeat2,
  UserRound,
} from "lucide-react";
import React, { type RefObject, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Process } from "../../api/types";
import { formatDuration } from "../../utils";
import type { ScreenshotRef } from "../screenshots";
import DurationAnalyticsSection from "./DurationAnalyticsSection";
import ProcessFindingsCarousel, { type ProcessFinding } from "./ProcessFindingsCarousel";
import { useProcessInstanceOptions } from "./ProcessInstancePicker";
import ProcessOccurrenceEvidence, { occurrenceActionClass } from "./ProcessOccurrenceEvidence";
import ProcessStepAppUsage from "./ProcessStepAppUsage";
import ProcessStepTransitions from "./ProcessStepTransitions";
import { ProcessStepTypesSection, StepTypeChips } from "./ProcessStepTypes";
import {
  type FlowLink,
  type ProcessEdge,
  type ProcessGraphNode,
  type ProcessOccurrenceSelection,
  type ProcessRenderGraph,
  type StepNamesById,
  buildFlow,
  durationObservationsForNode,
  occurrenceCount,
  terminalKind,
  totalDuration,
} from "./processGraphModel";
import { type Categories, stepTypeShares } from "./processGroups";
import {
  CollapsibleSidebarSection,
  ShowAllButton,
  StepAppIcon,
  panelScrollBehavior,
} from "./processSidebarUi";
import { runTitle } from "./processInstanceLabels";

type StepDetailsProps = {
  process: Process;
  node: ProcessGraphNode;
  graph: ProcessRenderGraph;
  instancesInScope: Process["instances"];
  onShowScreenshots: (screenshots: ScreenshotRef[]) => void;
  selectedOccurrence?: ProcessOccurrenceSelection;
  onChangeOccurrence: (id: string, occurrence: ProcessOccurrenceSelection) => void;
  edges: ProcessEdge[];
  reversedMap: Map<string, boolean>;
  nameById: StepNamesById;
  onAskBranching: (step: { id: string; name: string }, branch?: FlowLink) => void;
  onAskStep?: (occurrence?: ProcessOccurrenceSelection) => void;
  onHoverBranch?: (edge: { from: string; to: string } | null) => void;
  /** Comparison embeds just the selected occurrence, without aggregate analysis. */
  instanceContext?: boolean;
  onOpenInstanceView?: (instanceId: string, stepIndex: number) => void;
  onSelectStep?: (stepId: string) => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  /** Names step types; `null` while loading, which hides them. */
  categories?: Categories | null;
};

const StepDetails = ({
  process,
  node,
  graph,
  instancesInScope,
  onShowScreenshots,
  selectedOccurrence,
  onChangeOccurrence,
  edges,
  reversedMap,
  nameById,
  onAskBranching,
  onAskStep,
  onHoverBranch,
  instanceContext = false,
  onOpenInstanceView,
  onSelectStep,
  scrollContainerRef,
  categories,
}: StepDetailsProps) => {
  const { t } = useTranslation();
  const requestedIndex = selectedOccurrence
    ? node.instances.findIndex(
        occurrence =>
          occurrence.instanceId === selectedOccurrence.instanceId &&
          occurrence.stepIndex === selectedOccurrence.stepIndex,
      )
    : -1;
  const [occurrenceIndex, setOccurrenceIndex] = useState(Math.max(0, requestedIndex));
  const [expandedCase, setExpandedCase] = useState<string | null>(
    node.instances[Math.max(0, requestedIndex)]?.instanceId ?? null,
  );
  const evidenceRef = useRef<HTMLLIElement>(null);
  const [revealEvidence, setRevealEvidence] = useState(0);
  useLayoutEffect(() => {
    const scroller = scrollContainerRef?.current;
    const target = evidenceRef.current;
    if (revealEvidence === 0 || !scroller || !target) return;
    // Scroll only the details panel and retain the case heading above its evidence.
    const top =
      scroller.scrollTop +
      target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top -
      12;
    scroller.scrollTo({
      top: Math.max(0, Math.min(top, scroller.scrollHeight - scroller.clientHeight)),
      behavior: panelScrollBehavior(),
    });
  }, [revealEvidence, scrollContainerRef]);
  const [casesOpen, setCasesOpen] = useState(false);
  const [showAllCases, setShowAllCases] = useState(false);
  // External selection (including Back/Forward) reveals its case, even beyond the preview rows.
  useLayoutEffect(() => {
    const index = Math.max(0, requestedIndex);
    setOccurrenceIndex(index);
    setExpandedCase(node.instances[index]?.instanceId ?? null);
  }, [requestedIndex, node.instances]);
  const activeOccurrence = node.instances[occurrenceIndex];
  const stepTypes = useMemo(() => stepTypeShares(node.instances), [node.instances]);
  const matchingIds = useMemo(() => new Set(node.instances.map(item => item.instanceId)), [node]);
  const options = useProcessInstanceOptions(process, instancesInScope, matchingIds);
  const cases = useMemo(() => {
    // Occurrence indexes per case, in one pass rather than a scan of every occurrence per case.
    const indexesByCase = new Map<string, number[]>();
    node.instances.forEach((occurrence, index) => {
      const indexes = indexesByCase.get(occurrence.instanceId);
      if (indexes) indexes.push(index);
      else indexesByCase.set(occurrence.instanceId, [index]);
    });
    return [...options]
      .sort(
        (a, b) =>
          Date.parse(b.instance.source.traceStart) - Date.parse(a.instance.source.traceStart),
      )
      .map(option => {
        const indexes = indexesByCase.get(option.instance.id) ?? [];
        return {
          ...option,
          indexes,
          duration: indexes.reduce((sum, index) => sum + node.instances[index].duration, 0),
        };
      });
  }, [options, node.instances]);
  const activeCase = cases.find(row => row.instance.id === activeOccurrence?.instanceId);
  const duration = totalDuration(node);
  const coverage = instancesInScope.length
    ? Math.round((cases.length / instancesInScope.length) * 100)
    : 0;
  const repeatedCases = cases.filter(row => row.indexes.length > 1);
  const largestCase = cases.reduce<(typeof cases)[number] | undefined>(
    (largest, row) => (!largest || row.duration > largest.duration ? row : largest),
    undefined,
  );
  const concentration =
    largestCase && duration > 0 ? Math.round((largestCase.duration / duration) * 100) : 0;
  const flow = useMemo(
    () => buildFlow(edges, reversedMap, nameById, node.id),
    [edges, reversedMap, nameById, node.id],
  );
  const appById = useMemo(
    () => new Map(graph.nodes.map(item => [item.id, item.app ?? null])),
    [graph.nodes],
  );
  const observations = useMemo(() => durationObservationsForNode(node), [node]);

  const selectOccurrence = (index: number, reveal = false) => {
    if (reveal) {
      setCasesOpen(true);
      setRevealEvidence(value => value + 1);
    }
    const occurrence = node.instances[index];
    if (!occurrence) return;
    setOccurrenceIndex(index);
    setExpandedCase(occurrence.instanceId);
    onChangeOccurrence(node.id, {
      instanceId: occurrence.instanceId,
      stepIndex: occurrence.stepIndex,
    });
  };
  const findings: ProcessFinding[] = [];
  const findingAction = (label: string, onInspect: () => void) => (
    <button
      type="button"
      onClick={onInspect}
      className="inline-flex min-h-7 items-center gap-1 rounded text-xs font-medium text-info hover:underline focus-visible:ring-2 focus-visible:ring-info/30"
    >
      {label}
      <ChevronRight className="h-3 w-3" aria-hidden="true" />
    </button>
  );
  if (cases.length > 1 && concentration >= 50 && largestCase) {
    findings.push({
      id: "concentration",
      tone: "info",
      icon: PieChart,
      title: t("processPanel.stepConcentration", { pct: concentration }),
      body: t("processPanel.stepConcentrationDetail", {
        duration: formatDuration(largestCase.duration),
        count: largestCase.indexes.length,
      }),
      action: findingAction(t("processPanel.stepInspectCase"), () => {
        const occurrence = node.instances[largestCase.indexes[0]];
        if (onOpenInstanceView) {
          onOpenInstanceView(occurrence.instanceId, occurrence.stepIndex);
        } else {
          selectOccurrence(largestCase.indexes[0], true);
        }
      }),
    });
  }
  if (repeatedCases.length > 0) {
    findings.push({
      id: "repeated",
      tone: "variation",
      icon: Repeat2,
      title: t("processPanel.stepRepeated", { count: repeatedCases.length, total: cases.length }),
      body: t("processPanel.stepRepeatedDetail"),
      action: findingAction(t("processPanel.stepInspectOccurrences"), () =>
        selectOccurrence(repeatedCases[0].indexes[0], true),
      ),
    });
  }
  const evidence = activeOccurrence && (
    <ProcessOccurrenceEvidence
      key={`${activeOccurrence.instanceId}:${activeOccurrence.stepIndex}`}
      description={activeOccurrence.description}
      objects={activeOccurrence.objects}
      screenshots={activeOccurrence.screenshots.map(filename => ({
        recordingId: activeOccurrence.recordingId,
        userId: activeOccurrence.userId,
        filename,
      }))}
      onShowScreenshots={onShowScreenshots}
      onAsk={
        onAskStep
          ? () =>
              onAskStep({
                instanceId: activeOccurrence.instanceId,
                stepIndex: activeOccurrence.stepIndex,
              })
          : undefined
      }
    >
      {onOpenInstanceView && (
        <button
          type="button"
          className={occurrenceActionClass}
          onClick={() =>
            onOpenInstanceView(activeOccurrence.instanceId, activeOccurrence.stepIndex)
          }
        >
          {t("processPanel.stepOpenCase")}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </ProcessOccurrenceEvidence>
  );
  if (instanceContext)
    return (
      <div className="space-y-3 rounded-xl border border-outline bg-background-alt p-3">
        {activeOccurrence && (
          <p className="text-xs tabular-nums text-text-mid">
            {formatDuration(activeOccurrence.duration)}
          </p>
        )}
        {evidence}
      </div>
    );
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-outline bg-surface">
              <StepAppIcon app={node.app} className="h-6 w-6" fallback="generic" />
            </span>
            <div className="min-w-0">
              <h3 className="break-words text-base font-semibold text-text">{node.name}</h3>
              {node.app && <p className="mt-0.5 text-xs text-text-dim">{node.app}</p>}
              {categories && <StepTypeChips shares={stepTypes} categories={categories} />}
            </div>
          </div>
          {onAskStep && (
            <button
              type="button"
              className={`${occurrenceActionClass} shrink-0`}
              onClick={() => onAskStep()}
            >
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.caseAskStep")}
            </button>
          )}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-text-dim">
          {t("processPanel.stepScope", { count: instancesInScope.length })}
        </p>
      </header>
      <div className="overflow-hidden rounded-lg border border-outline bg-surface">
        <dl className="flex divide-x divide-outline py-3 text-xs">
          <div className="min-w-0 flex-1 px-3">
            <dt className="flex items-center gap-1.5 text-text-mid">
              <ListOrdered className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t("occurrences")}
            </dt>
            <dd className="mt-1 text-lg font-semibold leading-6 tabular-nums text-text">
              {node.instances.length}
            </dd>
          </div>
          <div className="min-w-0 flex-1 px-3">
            <dt className="flex items-center gap-1.5 text-text-mid">
              <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t("processPanel.metricCases")}
            </dt>
            <dd className="mt-1 flex flex-wrap items-baseline gap-x-1.5 tabular-nums text-text">
              <span className="text-lg font-semibold leading-6">{cases.length}</span>
              <span className="text-text-mid">
                / {instancesInScope.length} ({coverage}%)
              </span>
            </dd>
          </div>
          <div className="min-w-0 flex-1 px-3">
            <dt className="flex items-center gap-1.5 text-text-mid">
              <Clock3 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t("processPanel.stepTotalTime")}
            </dt>
            <dd className="mt-1 text-lg font-semibold leading-6 tabular-nums text-text">
              {formatDuration(duration)}
            </dd>
          </div>
        </dl>
      </div>
      <ProcessFindingsCarousel findings={findings} />
      {(flow.incoming.length > 0 || flow.outgoing.length > 0) && (
        <ProcessStepTransitions
          step={{ id: node.id, name: node.name }}
          start={graph.start}
          end={graph.end}
          incoming={flow.incoming}
          outgoing={flow.outgoing}
          appById={appById}
          onSelectStep={onSelectStep}
          onHoverBranch={onHoverBranch}
          onAsk={onAskBranching}
        />
      )}
      <DurationAnalyticsSection
        headerIcon={<Clock3 className="h-4 w-4" aria-hidden="true" />}
        observations={observations}
        countLabel={t("occurrences")}
      />
      {categories && <ProcessStepTypesSection shares={stepTypes} categories={categories} />}
      <ProcessStepAppUsage occurrences={node.instances} />
      <CollapsibleSidebarSection
        title={t("instances_through_step_title")}
        headerIcon={<Layers className="h-4 w-4" aria-hidden="true" />}
        summary={t("processPanel.stepCasesOrder")}
        titleAccessory={
          <span className="rounded-full bg-surface-strong px-2 py-0.5 text-xs font-medium tabular-nums text-text-mid">
            {cases.length}
          </span>
        }
        open={casesOpen}
        onOpenChange={setCasesOpen}
        separated={false}
        className="border-t border-outline pt-4"
      >
        <p className="mb-3 text-xs text-text-dim">{t("processPanel.stepCasesHint")}</p>
        <ul className="overflow-hidden rounded-lg border border-outline divide-y divide-outline">
          {cases
            .filter((row, index) => showAllCases || index < 3 || row.instance.id === expandedCase)
            .map((row, rowIndex) => {
              const expanded = row.instance.id === expandedCase;
              const title = runTitle(row.instance, row.identity.instanceLabel);
              const position = row.indexes.indexOf(occurrenceIndex);
              return (
                <li
                  key={row.instance.id}
                  ref={expanded ? evidenceRef : undefined}
                  className="overflow-hidden"
                >
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() =>
                      expanded ? setExpandedCase(null) : selectOccurrence(row.indexes[0])
                    }
                    className={`flex w-full items-start gap-2 border-l-2 p-3 text-left transition hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 ${expanded ? "border-info bg-focus-band/60" : `border-transparent ${rowIndex % 2 ? "bg-surface-strong/40" : "bg-surface"}`}`}
                  >
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 shrink-0 text-text-dim ${expanded ? "" : "-rotate-90"}`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className="line-clamp-2 break-words text-sm font-medium text-text"
                        title={title}
                      >
                        {title}
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-text-dim">
                        {row.instance.source.userName && (
                          <span className="inline-flex items-center gap-1">
                            <UserRound className="h-3 w-3" aria-hidden="true" />
                            {row.instance.source.userName}
                          </span>
                        )}
                        <time dateTime={row.instance.source.traceStart}>
                          {row.identity.startTime}
                        </time>
                        <span className="ml-auto inline-flex flex-wrap items-center justify-end gap-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 tabular-nums ${row.indexes.length > 1 ? "bg-variation/10 text-variation" : "text-text-mid"}`}
                          >
                            <Repeat2 className="h-3 w-3" aria-hidden="true" />
                            {t("processPanel.stepOccurrences", { count: row.indexes.length })}
                          </span>
                          <span className="flex w-40 shrink-0 flex-col gap-1.5 text-right tabular-nums">
                            <span className="inline-flex items-center justify-end gap-1 font-semibold text-info-strong">
                              <Clock3 className="h-3 w-3" aria-hidden="true" />
                              {t("processPanel.stepCaseTime", {
                                duration: formatDuration(row.duration),
                              })}
                            </span>
                            <span
                              className="h-1 w-full overflow-hidden rounded-full bg-info/10"
                              aria-hidden="true"
                            >
                              <span
                                className="block h-full min-w-[2px] rounded-full bg-info/60"
                                style={{
                                  width: `${largestCase && largestCase.duration > 0 ? (row.duration / largestCase.duration) * 100 : 0}%`,
                                }}
                              />
                            </span>
                          </span>
                        </span>
                      </span>
                    </span>
                  </button>
                  {expanded && activeCase?.instance.id === row.instance.id && activeOccurrence && (
                    <div className="space-y-3 border-t border-outline bg-focus-band/20 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-text-mid" aria-live="polite">
                          {t("processPanel.caseOccurrence", {
                            current: position + 1,
                            total: row.indexes.length,
                          })}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium tabular-nums text-text">
                            {formatDuration(activeOccurrence.duration)}
                          </span>
                          {row.indexes.length > 1 && (
                            <>
                              <button
                                type="button"
                                className={`${occurrenceActionClass} w-8 !px-0 disabled:opacity-40`}
                                aria-label={t("previous_occurrence")}
                                disabled={position === 0}
                                onClick={() => selectOccurrence(row.indexes[position - 1])}
                              >
                                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                className={`${occurrenceActionClass} w-8 !px-0 disabled:opacity-40`}
                                aria-label={t("next_occurrence")}
                                disabled={position === row.indexes.length - 1}
                                onClick={() => selectOccurrence(row.indexes[position + 1])}
                              >
                                <ChevronRight className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {evidence}
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
        {cases.length > 3 && (
          <ShowAllButton
            expanded={showAllCases}
            label={t("processPanel.stepAllCases", { count: cases.length })}
            onClick={() => setShowAllCases(value => !value)}
          />
        )}
      </CollapsibleSidebarSection>
    </div>
  );
};

export const NodeDetails = (props: StepDetailsProps) => {
  const { t } = useTranslation();
  const term = terminalKind(props.node, props.graph);
  return term !== null ? (
    <>
      <div className="mb-1 text-xs uppercase tracking-wide text-text-dim">
        {t(term === "start" ? "start" : "end")}
      </div>
      <div className="text-xs text-text-mid">
        {occurrenceCount(props.node)}{" "}
        {t(term === "start" ? "traces_begin_here" : "traces_end_here")}
      </div>
    </>
  ) : (
    <StepDetails key={props.node.id} {...props} />
  );
};
